import React, { useCallback } from 'react';
import { FirebaseImage, ImageSize, useFirebaseImage } from '../../Helpers/ImageLoader';
import { translate, translateOther } from '../../types/EnumTranslations';
import { ColorEnum, RoofSafetyStatus } from '../../types/Enums';
import { RoomModel, LevelModel, Building, BlueprintLocation, RoofAnchorPoint, RoofAccessPoint, BaseComponent } from '../../types/protocols';
import { Userprofile } from '../../Helpers/UsersContext';
import { BaseComponentImpl } from '../../types/implementations';
import { FieldChange } from '../../hooks/archive/useArchiveHistory';
import { findBlueprintImage } from '../../Helpers/blueprintLookup';
import {
    getAnchorPointColorFromString,
    ACCESS_POINT_COLOR,
    getScaledPositionForFixedContainer
} from '../../Helpers/RoofSafetyUtils';

// ── Shared helpers ───────────────────────────────────────────────

/** Safe JSON parse — returns null on failure. */
function tryParseJson(val: string | null): unknown {
    if (!val) return null;
    try { return JSON.parse(val); } catch { return null; }
}

/** Resolve a component key to a friendly display name.
 *  Falls back to [componentKey] if component not found. */
function resolveComponentDisplayName(
    componentKey: string | null,
    building: Building | null
): string {
    if (!componentKey) return '(okänd)';
    if (!building?.model?.structure?.components) return `[${componentKey}]`;

    const component = building.model.structure.components[componentKey] as BaseComponent | undefined;
    if (!component) return `[${componentKey}]`;

    return new BaseComponentImpl(component).displayName(building);
}

// ── Condition: "8/10" → colored badge ────────────────────────────

const ConditionBadge: React.FC<{ value: string | null }> = ({ value }) => {
    if (!value) return <span className="text-muted">(tom)</span>;
    const match = value.match(/^(\d+)\/10$/);
    if (!match) return <span>{value}</span>;
    const num = parseInt(match[1], 10);
    const hue = (num / 10) * 120; // 0=red, 120=green
    return (
        <span
            className="condition-badge"
            style={{ backgroundColor: `hsl(${hue}, 70%, 45%)` }}
        >
            {value}
        </span>
    );
};

// ── Color: swatch + translated name ──────────────────────────────

const COLOR_HEX: Record<string, string> = {
    color_white: '#FFFFFF', color_black: '#1a1a1a',
    color_lightGrey: '#B0B0B0', color_darkGrey: '#555555',
    color_beige: '#D4C5A9', color_yellow: '#F5D442',
    color_lightBlue: '#7EC8E3', color_darkBlue: '#2C5F8A',
    color_lightGreen: '#88C78A', color_darkGreen: '#2D6A2E',
    color_red: '#CC3333', color_faluRed: '#801818',
    color_brown: '#6B4226', color_terracotta: '#C4623A',
    color_pink: '#E8A0BF', color_purple: '#7B3F9E',
    color_orange: '#E88934', color_turquoise: '#3ABCB1',
};

const ColorValue: React.FC<{ value: string | null }> = ({ value }) => {
    if (!value) return <span className="text-muted">(tom)</span>;
    const parsed = tryParseJson(value) as { colorEnum?: string; code?: string } | null;
    if (!parsed) return <span>{value}</span>;

    const hex = parsed.code || (parsed.colorEnum ? COLOR_HEX[parsed.colorEnum] : undefined);
    const name = parsed.colorEnum
        ? translate(parsed.colorEnum as ColorEnum)
        : parsed.code || '?';

    return (
        <span className="color-value">
            {hex && (
                <span
                    className="color-swatch"
                    style={{ backgroundColor: hex }}
                />
            )}
            {name}
        </span>
    );
};

// ── Photos: thumbnails for added, count for removed ──────────────

const PhotosValue: React.FC<{ value: string | null; removed?: boolean }> = ({ value, removed }) => {
    if (!value) return <span className="text-muted">(tom)</span>;

    const parsed = tryParseJson(value);
    // photos is stored as { [key]: PhotoModel } — an object, not an array
    const photos: Array<{ storagePath?: string }> = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.values(parsed)
        : Array.isArray(parsed) ? parsed : [];

    const count = photos.length;
    if (count === 0) return <span className="text-muted">(tom)</span>;

    if (removed) {
        return <span>{count} {count === 1 ? 'foto borttaget' : 'foton borttagna'}</span>;
    }

    return (
        <div className="photos-grid">
            {photos.map((photo, i) => (
                photo.storagePath ? (
                    <FirebaseImage
                        key={i}
                        storagePath={photo.storagePath}
                        size={ImageSize.THUMBNAIL_SMALL}
                        alt=""
                        className="photo-thumbnail"
                    />
                ) : null
            ))}
            {count > 0 && <span className="photos-count">{count} {count === 1 ? 'foto' : 'foton'}</span>}
        </div>
    );
};

// ── Area shapes: total area in m² ────────────────────────────────

function computeAreaLabel(value: string | null): string {
    if (!value) return '(tom)';
    const parsed = tryParseJson(value);
    if (!Array.isArray(parsed) || parsed.length === 0) return '(tom)';

    // Each shape may have an `area` property, or we count shapes
    let totalArea = 0;
    let hasArea = false;
    for (const shape of parsed) {
        if (shape && typeof shape === 'object' && typeof shape.area === 'number') {
            totalArea += shape.area;
            hasArea = true;
        }
    }

    if (hasArea) {
        return `${totalArea.toFixed(1)} m²`;
    }
    return `${parsed.length} ${parsed.length === 1 ? 'yta' : 'ytor'}`;
}

// ── Blueprint / map locations: dot on image ─────────────────────

/** Resolve the storage path for a location image key. */
function resolveLocationImagePath(
    imageKey: string,
    field: string,
    building: Building | null,
): string | null {
    if (!building) return null;

    if (field === 'mapLocations') {
        return building.model?.mapImages?.[imageKey]?.storagePath ?? null;
    }

    // blueprintLocations — key is a blueprint key, on a level's drawing or the roof drawing
    return findBlueprintImage(building.model?.structure, imageKey)?.image.storagePath ?? null;
}

const LocationDot: React.FC<{
    value: string | null;
    field: string;
    building: Building | null;
}> = ({ value, field, building }) => {
    if (!value) return <span className="text-muted">(tom)</span>;
    const parsed = tryParseJson(value) as Record<string, { x?: number; y?: number; width?: number; height?: number }> | null;
    if (!parsed || typeof parsed !== 'object') return <span className="text-muted">(tom)</span>;

    const entries = Object.entries(parsed).filter(
        ([, loc]) => typeof loc?.x === 'number' && typeof loc?.y === 'number',
    );
    if (entries.length === 0) return <span className="text-muted">(tom)</span>;

    // Use the first entry's key to resolve the background image
    const imagePath = resolveLocationImagePath(entries[0][0], field, building);

    return (
        <div className="location-canvas-wrap">
            {imagePath ? (
                <FirebaseImage
                    storagePath={imagePath}
                    size={ImageSize.SCALED_SMALL}
                    alt=""
                    className="location-canvas-image"
                />
            ) : (
                <div className="location-canvas-placeholder" />
            )}
            <svg viewBox="0 0 100 100" className="location-canvas-overlay">
                {entries.map(([, loc], i) => {
                    const w = loc.width || 1;
                    const h = loc.height || 1;
                    const pctX = w > 1 ? (loc.x! / w) * 100 : loc.x! * 100;
                    const pctY = h > 1 ? (loc.y! / h) * 100 : loc.y! * 100;
                    return (
                        <circle
                            key={i}
                            cx={Math.min(Math.max(pctX, 2), 98)}
                            cy={Math.min(Math.max(pctY, 2), 98)}
                            r="4"
                            fill="#0d6efd"
                            stroke="#fff"
                            strokeWidth="1.5"
                        />
                    );
                })}
            </svg>
        </div>
    );
};

// ── Anchor/Access Points Map Visualization ───────────────────────

interface PointsMapVisualizationProps {
    oldValue: string | null;
    newValue: string | null;
    field: string;
    building: Building | null;
    userRow: React.ReactNode;
}

// Fixed container size for the history map preview
const POINTS_MAP_SIZE = 120;

const PointsMapVisualization: React.FC<PointsMapVisualizationProps> = ({
    oldValue,
    newValue,
    field,
    building,
    userRow
}) => {
    // Get map image
    const currentMapImageKey = building?.model?.currentMapImageKey;
    const mapImageRaw = currentMapImageKey && building?.model?.mapImages?.[currentMapImageKey];
    const mapImage = mapImageRaw && typeof mapImageRaw !== 'string' ? mapImageRaw : null;
    const { src: mapSrc, isLoading } = useFirebaseImage(mapImage?.storagePath);

    const parsePoints = useCallback((val: string | null): Array<{ key: string; position: BlueprintLocation; safetyStatus?: string }> => {
        if (!val) return [];
        const parsed = tryParseJson(val) as Record<string, RoofAnchorPoint | RoofAccessPoint> | null;
        if (!parsed || typeof parsed !== 'object') return [];

        return Object.entries(parsed)
            .filter(([, point]) => point?.position && typeof point.position.x === 'number' && typeof point.position.y === 'number')
            .map(([key, point]) => ({
                key,
                position: point.position as BlueprintLocation,
                safetyStatus: (point as RoofAnchorPoint).safetyStatus
            }));
    }, []);

    const getScaledPosition = useCallback((position: BlueprintLocation) => {
        return getScaledPositionForFixedContainer(position, POINTS_MAP_SIZE, mapImage);
    }, [mapImage]);

    const oldPoints = parsePoints(oldValue);
    const newPoints = parsePoints(newValue);

    const renderPointsMap = (points: Array<{ key: string; position: BlueprintLocation; safetyStatus?: string }>) => {
        const count = points.length;
        const pointLabel = field === 'accessPositions' ? 'takaccesspunkter' : 'fästpunkter';

        if (count === 0) {
            return <span className="text-muted">(tom)</span>;
        }

        if (!mapSrc) {
            return <span>{count} {pointLabel}</span>;
        }

        return (
            <div className="points-map-container">
                <div className="points-map-image-wrap">
                    <img src={mapSrc} alt="" className="points-map-image" />
                    {points.map((point) => {
                        const pos = getScaledPosition(point.position);
                        if (!pos) return null;

                        const color = field === 'anchorPoints'
                            ? getAnchorPointColorFromString(point.safetyStatus)
                            : ACCESS_POINT_COLOR;

                        return (
                            <div
                                key={point.key}
                                className="points-map-dot"
                                style={{
                                    left: pos.x,
                                    top: pos.y,
                                    backgroundColor: color,
                                }}
                            />
                        );
                    })}
                </div>
                <span className="points-map-count">{count} {pointLabel}</span>
            </div>
        );
    };

    if (isLoading) {
        return (
            <BeforeAfterRow
                before={<span className="text-muted">Laddar...</span>}
                after={<span className="text-muted">Laddar...</span>}
                userRow={userRow}
            />
        );
    }

    return (
        <BeforeAfterRow
            before={renderPointsMap(oldPoints)}
            after={renderPointsMap(newPoints)}
            userRow={userRow}
        />
    );
};

// ── Shared before/after layout ───────────────────────────────────

const BeforeAfterRow: React.FC<{
    before: React.ReactNode;
    after: React.ReactNode;
    userRow: React.ReactNode;
}> = ({ before, after, userRow }) => (
    <div>
        <div className="field-detail-visual-row">
            <div className="field-detail-side">
                <span className="field-detail-side-label">Före</span>
                {before}
            </div>
            <span className="field-detail-arrow">&rarr;</span>
            <div className="field-detail-side">
                <span className="field-detail-side-label">Efter</span>
                {after}
            </div>
        </div>
        {userRow}
    </div>
);

// ── Modal body dispatcher ────────────────────────────────────────

type ChangeAction = 'added' | 'changed' | 'removed';

function classifyChange(change: FieldChange): ChangeAction {
    const hasOld = change.oldValue != null && change.oldValue !== '';
    const hasNew = change.newValue != null && change.newValue !== '';
    if (!hasOld && hasNew) return 'added';
    if (hasOld && !hasNew) return 'removed';
    return 'changed';
}

const getUserName = (uid: string | null, users: Userprofile[] | undefined): string => {
    if (!uid) return 'Okänd användare';
    const user = users?.find(u => u.key === uid);
    return user?.name || 'Okänd användare';
};

export const FieldDetailBody: React.FC<{
    change: FieldChange;
    users: Userprofile[] | undefined;
    rooms?: { [key: string]: RoomModel };
    levels?: { [key: string]: LevelModel };
    building: Building | null;
    translateFieldValue: (field: string, value: string | null, rooms?: { [key: string]: RoomModel }, levels?: { [key: string]: LevelModel }) => string;
}> = ({ change, users, rooms, levels, building, translateFieldValue }) => {
    const { field, oldValue, newValue, changedBy } = change;
    const action = classifyChange(change);

    const userRow = changedBy ? (
        <p className="field-detail-user">Av: {getUserName(changedBy, users)}</p>
    ) : null;

    // ── Visual renderers ──────────────────────────────────────
    if (field === 'condition') {
        return <BeforeAfterRow before={<ConditionBadge value={oldValue} />} after={<ConditionBadge value={newValue} />} userRow={userRow} />;
    }

    if (field === 'color') {
        return <BeforeAfterRow before={<ColorValue value={oldValue} />} after={<ColorValue value={newValue} />} userRow={userRow} />;
    }

    if (field === 'photos') {
        return (
            <div>
                {action === 'removed' || (!newValue && oldValue) ? (
                    <div>
                        <span className="field-detail-side-label">Borttagna</span>
                        <PhotosValue value={oldValue} removed />
                    </div>
                ) : (
                    <>
                        {oldValue && (
                            <div className="mb-2">
                                <span className="field-detail-side-label">Före</span>
                                <PhotosValue value={oldValue} removed />
                            </div>
                        )}
                        <div>
                            <span className="field-detail-side-label">{oldValue ? 'Efter' : 'Tillagda'}</span>
                            <PhotosValue value={newValue} />
                        </div>
                    </>
                )}
                {userRow}
            </div>
        );
    }

    if (field === 'areaShapes') {
        return <BeforeAfterRow before={computeAreaLabel(oldValue)} after={computeAreaLabel(newValue)} userRow={userRow} />;
    }

    if (field === 'blueprintLocations' || field === 'mapLocations') {
        return (
            <BeforeAfterRow
                before={<LocationDot value={oldValue} field={field} building={building} />}
                after={<LocationDot value={newValue} field={field} building={building} />}
                userRow={userRow}
            />
        );
    }

    // ── Safety status: translated enum ─────────────────────────
    if (field === 'safetyStatus') {
        const translateStatus = (val: string | null): string => {
            if (!val) return '(tom)';
            // Try to parse as RoofSafetyStatus
            if (Object.values(RoofSafetyStatus).includes(val as RoofSafetyStatus)) {
                return translate(val as RoofSafetyStatus);
            }
            return val;
        };
        return (
            <BeforeAfterRow
                before={<span>{translateStatus(oldValue)}</span>}
                after={<span>{translateStatus(newValue)}</span>}
                userRow={userRow}
            />
        );
    }

    // ── Access positions / anchor points: show on map with status colors ─────────
    if (field === 'accessPositions' || field === 'anchorPoints') {
        return (
            <PointsMapVisualization
                oldValue={oldValue}
                newValue={newValue}
                field={field}
                building={building}
                userRow={userRow}
            />
        );
    }

    // ── Access info: plain text ──────────────────────────────
    if (field === 'accessInfo') {
        return (
            <BeforeAfterRow
                before={<span>{oldValue || '(tom)'}</span>}
                after={<span>{newValue || '(tom)'}</span>}
                userRow={userRow}
            />
        );
    }

    // ── Component replacement: show friendly component name ──
    if (field === 'replacedByKey' || field === 'replacedFromKey') {
        const componentKey = newValue || oldValue;
        const displayName = resolveComponentDisplayName(componentKey, building);
        const label = field === 'replacedByKey' ? 'Ersatt av' : 'Ersatte';

        return (
            <div className="p-3">
                <p className="mb-0">
                    {label}: <strong>{displayName}</strong>
                </p>
                {userRow}
            </div>
        );
    }

    // ── Default: text table ───────────────────────────────────
    return (
        <div>
            <table className="field-detail-table">
                <tbody>
                    <tr>
                        <td className="field-detail-label">Före</td>
                        <td className="field-detail-old">{translateFieldValue(field, oldValue, rooms, levels)}</td>
                    </tr>
                    <tr>
                        <td className="field-detail-label">Efter</td>
                        <td className="field-detail-new">{translateFieldValue(field, newValue, rooms, levels)}</td>
                    </tr>
                    {changedBy && (
                        <tr>
                            <td className="field-detail-label">Av</td>
                            <td>{getUserName(changedBy, users)}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};
